import React, { useState } from 'react';
import { Mail, Phone, MapPin, User, Globe } from 'lucide-react';
import { useSocialLinks } from '../hooks/useSocialLinks';
import { useCMSPage } from '../hooks/useCMSPage';
import { SEOHead } from '../components/SEOHead';
import './Contact.css';

// Icon mapping for CMS icons
const ICON_MAP = {
  mail: Mail,
  phone: Phone,
  'map-pin': MapPin,
  user: User,
  globe: Globe
};

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Use centralized CMS social links
  const { socialLinks, loading: socialLinksLoading } = useSocialLinks();
  
  // Use CMS page content
  const { 
    loading: cmsLoading, 
    getSectionContent, 
    getSectionBlocks, 
    isSectionVisible 
  } = useCMSPage('/contact');

  // Get CMS content with fallbacks
  const heroContent = getSectionContent('CONTACT_HERO') || {
    title: 'Get In Touch',
    subtitle: "Have a question, suggestion, or want to be a guest? We'd love to hear from you."
  };

  const formConfig = getSectionContent('CONTACT_FORM') || {
    heading: 'Send Us a Message',
    submitButtonText: 'Send Message',
    successMessage: "Thanks for reaching out! Your message has been sent to OfficialGolfNuts@gmail.com. We'll get back to you soon.",
    errorMessage: 'Something went wrong. Please try again or email us directly.'
  };

  // Get info cards from CMS
  const infoCardsBlocks = getSectionBlocks('CONTACT_INFO');
  const infoCards = infoCardsBlocks.length > 0 
    ? infoCardsBlocks.filter(block => block.isVisible !== false)
    : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render an info card based on CMS content
  const renderInfoCard = (block, index) => {
    const content = block.content || {};
    const IconComponent = content.icon ? ICON_MAP[content.icon] : null;
    
    // If showSocialLinks is true, render the social links card
    if (content.showSocialLinks) {
      return (
        <div key={block.id || index} className="contact-card">
          <h3 className="heading-3">{content.heading || 'Connect With Us'}</h3>
          <p className="body-medium">{content.description || 'Follow our journey on social media:'}</p>
          <div className="social-links-contact">
            {socialLinksLoading ? (
              <span className="social-loading">Loading...</span>
            ) : socialLinks.length > 0 ? (
              socialLinks.map((link) => {
                const SocialIcon = link.icon;
                if (!SocialIcon) return null;
                return (
                  <a 
                    key={link.platform}
                    href={link.platform === 'email' ? `mailto:${link.url}` : link.url}
                    target={link.platform !== 'email' ? '_blank' : undefined}
                    rel={link.platform !== 'email' ? 'noopener noreferrer' : undefined}
                    className="social-link-btn"
                  >
                    <SocialIcon size={24} />
                    <span>{link.name}</span>
                  </a>
                );
              })
            ) : (
              <span className="no-social">No social links available</span>
            )}
          </div>
        </div>
      );
    }

    // Regular info card
    return (
      <div key={block.id || index} className="contact-card">
        {IconComponent && <IconComponent className="contact-icon" size={32} />}
        <h3 className="heading-3">{content.heading}</h3>
        <p className="body-medium">{content.description}</p>
        {content.linkText && content.linkUrl && (
          <a href={content.linkUrl} className="contact-link">{content.linkText}</a>
        )}
      </div>
    );
  };

  // Fallback info cards if CMS is empty
  const renderFallbackInfoCards = () => (
    <>
      <div className="contact-card">
        <Mail className="contact-icon" size={32} />
        <h3 className="heading-3">Email Us</h3>
        <p className="body-medium">For general inquiries, guest appearances, or sponsorship opportunities:</p>
        <a href="mailto:OfficialGolfNuts@gmail.com" className="contact-link">OfficialGolfNuts@gmail.com</a>
      </div>
      
      <div className="contact-card">
        <h3 className="heading-3">Want to be a Guest?</h3>
        <p className="body-medium">
          We're always looking for interesting people with great golf stories. 
          Whether you're a business leader, athlete, or have a unique perspective on the game, 
          we want to hear from you.
        </p>
      </div>
      
      <div className="contact-card">
        <h3 className="heading-3">Connect With Us</h3>
        <p className="body-medium">Follow our journey on social media:</p>
        <div className="social-links-contact">
          {socialLinksLoading ? (
            <span className="social-loading">Loading...</span>
          ) : socialLinks.length > 0 ? (
            socialLinks.map((link) => {
              const IconComponent = link.icon;
              if (!IconComponent) return null;
              return (
                <a 
                  key={link.platform}
                  href={link.platform === 'email' ? `mailto:${link.url}` : link.url}
                  target={link.platform !== 'email' ? '_blank' : undefined}
                  rel={link.platform !== 'email' ? 'noopener noreferrer' : undefined}
                  className="social-link-btn"
                >
                  <IconComponent size={24} />
                  <span>{link.name}</span>
                </a>
              );
            })
          ) : (
            <span className="no-social">No social links available</span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="contact-page">
      <SEOHead
        title="Contact"
        description="Get in touch with the Golf Nuts crew — for guest pitches, sponsorships, partnerships, or just to say hi."
        path="/contact"
      />
      {/* Hero Section */}
      {isSectionVisible('CONTACT_HERO') && (
        <div className="contact-hero">
          <div className="container">
            <h1 className="display-medium">{heroContent.title}</h1>
            <p className="hero-subtitle-text">{heroContent.subtitle}</p>
          </div>
        </div>
      )}

      <div className="container contact-content">
        <div className="contact-grid">
          {/* Contact Form */}
          {isSectionVisible('CONTACT_FORM') && (
            <div className="contact-form-section">
              <h2 className="heading-2">{formConfig.heading}</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="What's this about?"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-textarea"
                    placeholder="Tell us what's on your mind..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn-primary btn-large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : formConfig.submitButtonText}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="success-message">
                    {formConfig.successMessage}
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    {formConfig.errorMessage}
                  </div>
                )}
              </form>
            </div>
          )}

          {/* Contact Info */}
          {isSectionVisible('CONTACT_INFO') && (
            <div className="contact-info-section">
              {infoCards.length > 0 
                ? infoCards.map((block, index) => renderInfoCard(block, index))
                : renderFallbackInfoCards()
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
